import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import WavesurferPlayer from "@wavesurfer/react";
import type WaveSurfer from "wavesurfer.js";
import { getCompositionDurationMs } from "@repo/video-engine-i2v/utils/timing";
import MusicOptionsDropdown from "./MusicOptionsDropdown";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { Button } from "@/lib/components/Button/Button";
import { IconRotateClockwise } from "@tabler/icons-react";
import { useBackgroundMusicsByExtIdsQuery } from "@/lib/queries/background-music-queries";

type HandleKind = "start" | "fadeInEnd" | "fadeOutStart";

const HANDLE_MIN_GAP_MS = 50;
const ZOOM_LEVELS = [1, 2, 4, 8, 16] as const;
const MIN_ZOOM = ZOOM_LEVELS[0];
const MAX_ZOOM = ZOOM_LEVELS[ZOOM_LEVELS.length - 1] as number;

function formatDuration(ms: number | null): string {
  if (ms === null) return "";
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export default function MusicTrimAndFadeControl() {
  const [isLoading, setIsLoading] = useState(true);
  const [songDurationMs, setSongDurationMs] = useState<number | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const [isDraggingStart, setIsDraggingStart] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const hasAutoRetriedRef = useRef(false);
  const compositionBackgroundMusic = useEditorStore(
    (state) => state.video?.compositionJson.backgroundMusic,
  );
  const composition = useEditorStore((state) => state.video?.compositionJson);
  const animatedImages = useEditorStore((state) => state.animatedImages);
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const updateVideo = useEditorStore((state) => state.service.updateVideo);

  const externalId = compositionBackgroundMusic?.externalId;
  const { data: backgroundMusics = [] } = useBackgroundMusicsByExtIdsQuery(
    teamExternalId,
    externalId ? [externalId] : [],
  );
  const track = backgroundMusics[0];

  const videoDurationMs = composition
    ? getCompositionDurationMs(composition, animatedImages)
    : 0;

  useEffect(() => {
    setIsLoading(true);
    setSongDurationMs(null);
    wavesurferRef.current = null;
    setZoom(1);
    setHasError(false);
    setRetryNonce(0);
    hasAutoRetriedRef.current = false;
  }, [track?.src]);

  const startMs = compositionBackgroundMusic?.startMs ?? 0;
  const fadeInMs = compositionBackgroundMusic?.fadeInMs ?? 0;
  const fadeOutMs = compositionBackgroundMusic?.fadeOutMs ?? 1000;
  const startSilenceMs = compositionBackgroundMusic?.startSilenceMs ?? 0;
  const endSilenceMs = compositionBackgroundMusic?.endSilenceMs ?? 0;

  const audibleWindowMs = Math.max(
    0,
    videoDurationMs - startSilenceMs - endSilenceMs,
  );
  const activeStartMs = startMs;
  const activeEndMs =
    songDurationMs !== null
      ? Math.min(songDurationMs, activeStartMs + audibleWindowMs)
      : activeStartMs + audibleWindowMs;

  const startStop =
    songDurationMs && songDurationMs > 0
      ? Math.max(0, Math.min(1, activeStartMs / songDurationMs))
      : 0;
  const endStop =
    songDurationMs && songDurationMs > 0
      ? Math.max(0, Math.min(1, activeEndMs / songDurationMs))
      : 1;

  const isApplyingGradientRef = useRef(false);

  const applyGradient = (ws: WaveSurfer, sStop: number, eStop: number) => {
    if (isApplyingGradientRef.current) return;
    const wrapper = ws.getWrapper();
    const waveWrapper = wrapper.querySelector(".canvases");
    const canvases = Array.from(
      (waveWrapper ?? wrapper).querySelectorAll("canvas"),
    ) as HTMLCanvasElement[];
    if (canvases.length === 0) return;

    const totalDeviceWidth = canvases.reduce((sum, c) => sum + c.width, 0);
    if (totalDeviceWidth <= 0) return;

    const inactiveColor = "#e5e7eb";
    const activeStart = "#f98c5e";
    const activeEnd = "#f66c97";

    isApplyingGradientRef.current = true;
    try {
      let offsetDevice = 0;
      for (const canvas of canvases) {
        const ctx = canvas.getContext("2d");
        if (ctx && canvas.width > 0) {
          const gradient = ctx.createLinearGradient(
            -offsetDevice,
            0,
            totalDeviceWidth - offsetDevice,
            0,
          );
          gradient.addColorStop(0, inactiveColor);
          if (sStop > 0) gradient.addColorStop(sStop, inactiveColor);
          gradient.addColorStop(sStop, activeStart);
          gradient.addColorStop(eStop, activeEnd);
          if (eStop < 1) gradient.addColorStop(eStop, inactiveColor);
          gradient.addColorStop(1, inactiveColor);

          ctx.save();
          ctx.globalCompositeOperation = "source-in";
          ctx.fillStyle = gradient;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.restore();
        }
        offsetDevice += canvas.width;
      }
    } finally {
      isApplyingGradientRef.current = false;
    }
  };

  const startStopRef = useRef(startStop);
  const endStopRef = useRef(endStop);
  startStopRef.current = startStop;
  endStopRef.current = endStop;

  useEffect(() => {
    const ws = wavesurferRef.current;
    if (ws && !isLoading) {
      applyGradient(ws, startStop, endStop);
    }
  }, [startStop, endStop, isLoading]);

  const onReady = (ws: WaveSurfer) => {
    wavesurferRef.current = ws;
    const durationMs = Math.round(ws.getDuration() * 1000);
    setSongDurationMs(durationMs);
    setIsLoading(false);
    setHasError(false);
    const initialStartStop = Math.max(
      0,
      Math.min(1, startMs / Math.max(1, durationMs)),
    );
    const initialActiveEnd = Math.min(startMs + audibleWindowMs, durationMs);
    const initialEndStop = Math.max(
      0,
      Math.min(1, initialActiveEnd / Math.max(1, durationMs)),
    );
    startStopRef.current = initialStartStop;
    endStopRef.current = initialEndStop;
    ws.on("redrawcomplete", () => {
      applyGradient(ws, startStopRef.current, endStopRef.current);
      const wrapper = ws.getWrapper();
      if (wrapper.style.visibility === "hidden") {
        wrapper.style.visibility = "";
      }
    });
    queueMicrotask(() => applyGradient(ws, initialStartStop, initialEndStop));
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      const ws = wavesurferRef.current;
      if (ws) applyGradient(ws, startStopRef.current, endStopRef.current);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const wrapper = scrollWrapperRef.current;
    const container = containerRef.current;
    if (!wrapper || !container || !songDurationMs) return;
    const startFrac = Math.max(0, Math.min(1, startMs / songDurationMs));
    const targetLeft =
      startFrac * container.clientWidth - wrapper.clientWidth / 2;
    wrapper.scrollLeft = Math.max(
      0,
      Math.min(container.clientWidth - wrapper.clientWidth, targetLeft),
    );
    const ws = wavesurferRef.current;
    if (ws) {
      ws.getWrapper().style.visibility = "hidden";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  const toPercent = useCallback(
    (ms: number) => {
      if (!songDurationMs || songDurationMs <= 0) return 0;
      return Math.max(0, Math.min(100, (ms / songDurationMs) * 100));
    },
    [songDurationMs],
  );

  const handlePointerDown = (kind: HandleKind) => (e: React.PointerEvent) => {
    if (!songDurationMs || !containerRef.current) return;
    e.preventDefault();
    if (kind === "start") setIsDraggingStart(true);
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();

    const compute = (clientX: number) => {
      const ratio = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width),
      );
      return Math.round(ratio * songDurationMs);
    };

    const onMove = (ev: PointerEvent) => {
      const ms = compute(ev.clientX);
      updateVideo((video) => {
        const bm = video.compositionJson.backgroundMusic;
        if (!bm) return;
        const curStart = bm.startMs ?? 0;
        const curFadeIn = bm.fadeInMs ?? 0;
        const curFadeOut = bm.fadeOutMs ?? 0;
        const curStartSilence = bm.startSilenceMs ?? 0;
        const curEndSilence = bm.endSilenceMs ?? 0;
        const curWindow = Math.max(
          0,
          videoDurationMs - curStartSilence - curEndSilence,
        );

        if (kind === "start") {
          const maxStart = Math.max(0, songDurationMs - HANDLE_MIN_GAP_MS);
          bm.startMs = Math.max(0, Math.min(ms, maxStart));
        } else if (kind === "fadeInEnd") {
          const activeStart = curStart;
          const newFadeIn = Math.max(0, Math.min(ms - activeStart, curWindow));
          bm.fadeInMs = newFadeIn;
          if (curFadeOut > curWindow - newFadeIn) {
            bm.fadeOutMs = Math.max(0, curWindow - newFadeIn);
          }
        } else if (kind === "fadeOutStart") {
          const activeEnd = curStart + curWindow;
          const newFadeOut = Math.max(0, Math.min(activeEnd - ms, curWindow));
          bm.fadeOutMs = newFadeOut;
          if (curFadeIn > curWindow - newFadeOut) {
            bm.fadeInMs = Math.max(0, curWindow - newFadeOut);
          }
        }
      });
    };

    const onUp = () => {
      if (kind === "start") setIsDraggingStart(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const handleRetry = () => {
    setHasError(false);
    setIsLoading(true);
    setSongDurationMs(null);
    wavesurferRef.current = null;
    setRetryNonce((nonce) => nonce + 1);
  };

  if (!compositionBackgroundMusic || !track) {
    return null;
  }

  const naturalEndMs =
    songDurationMs !== null
      ? Math.min(songDurationMs, startMs + videoDurationMs)
      : startMs + videoDurationMs;

  const startPct = toPercent(startMs);
  const endPct = toPercent(naturalEndMs);
  const activeStartPct = toPercent(activeStartMs);
  const activeEndPct = toPercent(activeEndMs);
  const fadeInEndPct = toPercent(activeStartMs + fadeInMs);
  const fadeOutStartPct = toPercent(activeEndMs - fadeOutMs);

  return (
    <div className="w-full">
      <div className="flex items-center gap-2">
        <div className="flex flex-1 flex-col">
          <div className="mb-2.5 flex items-end justify-between gap-2 px-0">
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="input-label mb-1 truncate">{track.name}</div>
              <span className="line-clamp-2 text-sm text-text-secondary">
                Drag to set the music section used in the video.
              </span>
            </div>
            <ZoomControls
              zoom={zoom}
              onZoomIn={() => {
                const next = ZOOM_LEVELS.find((z) => z > zoom);
                if (next !== undefined) setZoom(next);
              }}
              onZoomOut={() => {
                const prev = [...ZOOM_LEVELS].reverse().find((z) => z < zoom);
                if (prev !== undefined) setZoom(prev);
              }}
              onZoomReset={() => setZoom(1)}
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="group/waveform relative flex min-h-13 flex-1 items-center gap-0 rounded-md border border-grey-200 pl-2">
              <div className="relative flex-1">
                {isLoading && !hasError && (
                  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-start pl-3">
                    <LoadingSpinner />
                  </div>
                )}
                {hasError && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center gap-0.5 bg-white">
                    <span className="text-sm text-error">
                      Song failed to load
                    </span>
                    <Button
                      variant="ghost"
                      size="iconSm"
                      aria-label="Retry loading song"
                      onClick={handleRetry}
                    >
                      <IconRotateClockwise
                        className="rotate-180 text-error"
                        size={20}
                      />
                    </Button>
                  </div>
                )}
                <div
                  ref={scrollWrapperRef}
                  className="relative mr-1 overflow-x-auto py-4 pb-4 pl-3"
                >
                  <div
                    ref={containerRef}
                    className="relative touch-none select-none"
                    style={{ width: `${zoom * 100}%` }}
                  >
                    <WavesurferPlayer
                      key={`${track.src}-${retryNonce}`}
                      height={48}
                      waveColor="#e5e7eb"
                      progressColor="#f98c5e"
                      url={track.src}
                      peaks={track.peaks ?? undefined}
                      onReady={onReady}
                      onError={(error) => {
                        console.error(
                          "[MusicTrimAndFadeControl] wavesurfer error",
                          error,
                        );
                        if (!hasAutoRetriedRef.current) {
                          hasAutoRetriedRef.current = true;
                          handleRetry();
                          return;
                        }
                        setIsLoading(false);
                        setHasError(true);
                      }}
                      barWidth={1.5}
                      barGap={3}
                      barRadius={3}
                      cursorColor="transparent"
                      normalize={true}
                      interact={false}
                    />

                    {!isLoading && songDurationMs !== null && (
                      <>
                        {startPct > 0 && (
                          <div
                            className="pointer-events-none absolute inset-y-0 left-0"
                            style={{
                              width: `${startPct}%`,
                              backdropFilter: "grayscale(100%)",
                              WebkitBackdropFilter: "grayscale(100%)",
                            }}
                          />
                        )}
                        {endPct < 100 && (
                          <div
                            className="pointer-events-none absolute inset-y-0"
                            style={{
                              left: `${endPct}%`,
                              right: 0,
                              backdropFilter: "grayscale(100%)",
                              WebkitBackdropFilter: "grayscale(100%)",
                            }}
                          />
                        )}

                        {fadeInEndPct > activeStartPct && (
                          <div
                            className="pointer-events-none absolute inset-y-0 z-10"
                            style={{
                              left: `calc(${activeStartPct}% - 3px)`,
                              width: `calc(${fadeInEndPct - activeStartPct}%)`,
                              backgroundColor: "rgba(255, 255, 255, 0.75)",
                              clipPath: "polygon(0 0, 100% 0, 0 100%)",
                            }}
                          />
                        )}
                        {activeEndPct > fadeOutStartPct && (
                          <div
                            className="pointer-events-none absolute inset-y-0 z-10"
                            style={{
                              left: `${fadeOutStartPct}%`,
                              width: `calc(${activeEndPct - fadeOutStartPct}%)`,
                              backgroundColor: "rgba(255, 255, 255, 0.75)",
                              clipPath: "polygon(0 0, 100% 0, 100% 100%)",
                            }}
                          />
                        )}

                        <TrimHandle
                          label="Start"
                          position={startPct}
                          dragging={isDraggingStart}
                          valueLabel={formatDuration(startMs)}
                          onPointerDown={handlePointerDown("start")}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="mr-2 flex w-8 shrink-0 items-center justify-center">
                <span className="font-regular shrink-0 text-sm text-text-primary">
                  {formatDuration(songDurationMs)}
                </span>
              </div>
            </div>
            <MusicOptionsDropdown videoDurationMs={videoDurationMs} />
          </div>
        </div>
      </div>
    </div>
  );
}

function TrimHandle({
  label,
  position,
  dragging = false,
  valueLabel,
  onPointerDown,
}: {
  label: string;
  position: number;
  dragging?: boolean;
  valueLabel?: string;
  onPointerDown: (e: React.PointerEvent) => void;
}) {
  const handleRef = useRef<HTMLDivElement | null>(null);
  const [labelPos, setLabelPos] = useState<{ x: number; y: number } | null>(
    null,
  );

  useLayoutEffect(() => {
    if (!dragging || !valueLabel || !handleRef.current) {
      setLabelPos(null);
      return;
    }
    const rect = handleRef.current.getBoundingClientRect();
    setLabelPos({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });
  }, [dragging, valueLabel, position]);

  return (
    <div
      ref={handleRef}
      role="slider"
      aria-label={label}
      onPointerDown={onPointerDown}
      className="group absolute inset-y-0 z-10 w-4 -translate-x-1/2 cursor-ew-resize"
      style={{ left: `calc(${position}% - 8px)` }}
    >
      <div
        className={`absolute inset-y-1 left-1/2 w-1 -translate-x-1/2 rounded-full border border-grey-400 bg-white transition-opacity group-hover/waveform:opacity-100 ${dragging ? "opacity-100" : "opacity-60"}`}
      />
      {dragging &&
        valueLabel &&
        labelPos &&
        createPortal(
          <div
            className="pointer-events-none fixed z-200 -translate-x-full -translate-y-1/2 rounded bg-black/70 px-1.5 py-0.5 text-xs font-medium whitespace-nowrap text-white tabular-nums"
            style={{ left: labelPos.x - 6, top: labelPos.y }}
          >
            {valueLabel}
          </div>,
          document.body,
        )}
    </div>
  );
}

function ZoomControls({
  zoom,
  disabled = false,
  onZoomIn,
  onZoomOut,
  onZoomReset,
}: {
  zoom: number;
  disabled?: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
}) {
  const atMin = zoom <= MIN_ZOOM;
  const atMax = zoom >= MAX_ZOOM;
  return (
    <div className="flex items-center gap-1 pr-13 text-xs text-text-secondary">
      <button
        type="button"
        aria-label="Zoom out"
        onClick={onZoomOut}
        disabled={disabled || atMin}
        className="flex h-6 w-6 items-center justify-center rounded border border-grey-200 leading-none hover:bg-grey-100 disabled:cursor-not-allowed disabled:opacity-40"
      >
        −
      </button>
      <button
        type="button"
        aria-label="Reset zoom"
        onClick={onZoomReset}
        disabled={disabled || atMin}
        className="h-6 min-w-9 rounded border border-grey-200 px-1.5 py-0.5 tabular-nums hover:bg-grey-100 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {zoom}x
      </button>
      <button
        type="button"
        aria-label="Zoom in"
        onClick={onZoomIn}
        disabled={disabled || atMax}
        className="flex h-6 w-6 items-center justify-center rounded border border-grey-200 leading-none hover:bg-grey-100 disabled:cursor-not-allowed disabled:opacity-40"
      >
        +
      </button>
    </div>
  );
}
